import React from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Leaf,
  Globe2,
  BatteryCharging,
  Briefcase,
  Users,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";

// Replace these with your actual image imports
import Drone1 from "../assets/drone1.jpg";
import Drone2 from "../assets/drone2.jpg";
import Drone3 from "../assets/drone3.jpg";
import Drone4 from "../assets/drone4.jpg";

export const Drone = () => {
  return (
    <MainLayout>
      {/* Hero Section */}
      <section
        className="relative h-[60vh] rounded-3xl mx-4 sm:mx-10 mt-6 overflow-hidden shadow-xl bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${Drone1})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative z-10 max-w-3xl text-center px-6"
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-2 just-me-again-down-here-regular">
            Eco-Conscious Hybrid Van
          </h1>
          <div className="text-2xl">
            Smart, Sustainable & Comfortable – The Future of Travel in Uganda.
          </div>
        </motion.div>
      </section>

      {/* Intro Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed"
        >
          Our uniquely designed hybrid van is not just eco-friendly — it's built
          for comfort, exploration, and utility. Perfect for scenic drives,
          NGO fieldwork, or light cargo, it blends efficiency, reliability, and
          modern travel convenience.
        </motion.p>

        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="list-disc list-inside space-y-3 text-gray-700 dark:text-gray-300 text-md"
        >
          <li>Eco-conscious vehicle design</li>
          <li>Multiple power sockets</li>
          <li>Spacious seating with recline options</li>
          <li>Smooth, quiet engine</li>
          <li>Ideal for tours and light transport</li>
        </motion.ul>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mt-8 text-sm italic text-gray-600 dark:text-gray-400"
        >
          Note: The hybrid van is available on request and subject to
          availability. Advanced booking is recommended.
        </motion.p>
      </section>

      {/* Why Choose Our Hybrid Van */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-bold text-center mb-12 just-me-again-down-here-regular"
        >
          Why Choose Our Hybrid Van?
        </motion.h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            {
              icon: <Leaf className="w-10 h-10 text-teal-600" />,
              title: "Eco-Friendly Efficiency",
              desc: "Reduce your carbon footprint without compromising performance. Travel cleaner and help protect Uganda’s stunning natural environment.",
            },
            {
              icon: <Globe2 className="w-10 h-10 text-teal-600" />,
              title: "Built for Fieldwork & Exploration",
              desc: "Whether you're crossing borders, visiting remote areas, or on NGO duty, our van is rugged, efficient, and reliable.",
            },
            {
              icon: <BatteryCharging className="w-10 h-10 text-teal-600" />,
              title: "Comfort That Works for You",
              desc: "Stay powered and productive with built-in sockets, interior lighting, and air conditioning for long journeys.",
            },
            {
              icon: <Users className="w-10 h-10 text-teal-600" />,
              title: "Spacious & Versatile",
              desc: "Plenty of room for your team, gear, and luggage — without sacrificing comfort or space.",
            },
            {
              icon: <HeartHandshake className="w-10 h-10 text-teal-600" />,
              title: "Perfect for Conscious Travellers",
              desc: "Designed for clients who value impact and innovation, supporting a smarter, greener way to explore East Africa.",
            },
            {
              icon: <Briefcase className="w-10 h-10 text-teal-600" />,
              title: "Power Meets Purpose",
              desc: "This isn’t just a vehicle. It’s part of our mission to redefine mobility — blending technology, sustainability, and service in one smooth ride.",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 shadow-lg hover:scale-[1.02] transition"
            >
              <div className="flex justify-center mb-4">{item.icon}</div>
              <h3 className="text-xl font-semibold text-center mb-2 just-me-again-down-here-regular">
                {item.title}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-center">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto text-center mt-10 text-gray-700 dark:text-gray-300 text-lg"
        >
          Let us help you travel cleaner, go further, and arrive in comfort.
        </motion.p>
      </section>

      {/* CTA Section */}
      <section className="text-center py-16 px-6 bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-3xl mx-4 sm:mx-10 my-10 shadow-2xl">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-5xl font-bold mb-6 just-me-again-down-here-regular"
        >
          Travel Smart. Travel Green.
        </motion.h3>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-4"
        >
          <Link
            to="/gallery"
            className="bg-white text-teal-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition flex items-center gap-2"
          >
            More About the Hybrid Van <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/contact"
            className="bg-teal-600 hover:bg-teal-500 px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
          >
            Book the Eco Van
          </Link>
          <Link
            to="/blog/eco-friendly"
            className="bg-white text-teal-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition flex items-center gap-2"
          >
            Why Eco-Friendly Travel Matters
          </Link>
        </motion.div>
      </section>

      {/* Gallery */}
      <section className="max-w-6xl mx-auto px-6 mb-24">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center mb-10 just-me-again-down-here-regular"
        >
          A Closer Look at the Hybrid Van
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {[Drone1, Drone2, Drone3, Drone4].map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className="rounded-xl overflow-hidden shadow-lg border border-white/20 dark:border-white/10 backdrop-blur-md"
            >
              <img
                src={src}
                alt={`Drone ${i + 1}`}
                className="w-full h-48 object-cover"
              />
            </motion.div>
          ))}
        </div>
      </section>
    </MainLayout>
  );
};

